// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## SolfegeSystem
//
// An object for converting between scale degrees and solfege.
import type {
  SolfegeSystemName,
  ScaleDegree,
} from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import { solfegeSystems } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import utils from '../utils';

export default class SolfegeSystem {
  system: SolfegeSystemName;

  // Public constructor
  // System names are:
  //    scaleDegrees (default)
  //    solfegeDoMinor
  //    solfegeLaMinor
  constructor(system = 'scaleDegrees') {
    if (!SolfegeSystem.isValidSystemName(system)) {
      throw new Error(
        `${system} is not a valid system name. Must be scaleDegrees, solfegeDoMinor or solfegeLaMinor`
      );
    }
    this.system = system;
  }

  degreeToSolfege(degree: ScaleDegree, mode: 'Major' | 'minor', system = this.system) {
    return SolfegeSystem.degreeToSolfege(degree, mode, system);
  }

  solfegeToDegree(degree, mode, system) {
    system = system || this.system;
    return SolfegeSystem.solfegeToDegree(degree, mode, system);
  }

  /**
   * @param options {
   *   minorType: {bool} false -- show "La minor" or "Do minor"
   *   upperCase: {bool} false -- "scale degrees" vs "Scale Degrees"
   */
  toString(options: { minorType?: boolean; upperCase?: boolean } = {}) {
    options = options || {};
    let systemName = {
      scaleDegrees: ['scale degrees', 'scale degrees'],
      solfegeDoMinor: ['solfege', 'solfege, Do minor'],
      solfegeLaMinor: ['solfege', 'solfege, La minor'],
    }[this.system][options.minorType ? 1 : 0];

    if (options.upperCase) {
      systemName = systemName
        .split(' ')
        .map((word) => word[0].toUpperCase() + word.slice(1))
        .join(' ');
    }

    return systemName;
  }

  static isValidSystemName(system: string): system is SolfegeSystemName {
    return ['scaleDegrees', 'solfegeDoMinor', 'solfegeLaMinor'].includes(system);
  }

  static degreeToSolfege = (
    degree: ScaleDegree,
    mode: 'Major' | 'minor',
    system: SolfegeSystemName
  ): string | number => {
    if (typeof system === 'undefined') throw new Error('system undefined');
    if (typeof mode === 'undefined') mode = 'Major';
    if (typeof degree === 'undefined') throw new Error('NaN: Must supply scale degree.');

    const result = solfegeSystems[mode][system][degree];

    if (typeof result === 'undefined')
      throw new Error(
        'Out of Bounds: Degree, mode, or system arguments were invalid; no solfege found'
      );

    return result;
  };

  static solfegeToDegree = (
    solfege: string,
    mode: 'Major' | 'minor',
    system: SolfegeSystemName
  ): string | number => {
    if (typeof system === 'undefined') throw new Error('system undefined');
    if (typeof mode === 'undefined') mode = 'Major';
    if (typeof solfege === 'undefined') throw new Error('NaN: Must supply scale degree.');

    const scaleSolfege = solfegeSystems[mode][system];
    const result = utils.arraySearch(solfege, scaleSolfege);

    if (typeof result === 'undefined')
      throw new Error(
        'Out of Bounds: Degree, mode, or system arguments were invalid; no solfege found'
      );

    return utils.isNumeric(result) ? Number(result) : result;
  };
}
